import {
  ACCURACY_THRESHOLD_EXCELLENT,
  ACCURACY_THRESHOLD_MISS,
  getScoreColor,
} from "@/components/readonly/scoring";
import { Skeleton } from "@/components/shared/Skeleton";
import { AppButton } from "@/components/teacher/AppButton";
import { AppDialog } from "@/components/teacher/AppDialog";
import {
  ClassroomLessonMatrixDocument,
  type ClassroomLessonMatrixQuery,
} from "@/generated/graphql";
import { type TranslationKey, format, useT } from "@/i18n";
import { useQuery } from "@apollo/client/react";

type MatrixRow = ClassroomLessonMatrixQuery["classroomLessonMatrix"][number];

/** Nothing scored yet. Deliberately not a colour — "not started" must not read as a fourth band. */
const NOT_STARTED_COLOR = "#A3A3A3";
const NO_SCORE_LABEL = "—";
const SKELETON_ROWS = ["a", "b", "c", "d", "e", "f"];

/** One dot per band, coloured by a score inside it, so the legend cannot drift from the cells. */
const LEGEND: { key: TranslationKey; average: number | null }[] = [
  { key: "selfStudy.band.strong", average: ACCURACY_THRESHOLD_EXCELLENT },
  { key: "selfStudy.band.mixed", average: ACCURACY_THRESHOLD_MISS },
  { key: "selfStudy.band.weak", average: 0 },
  { key: "selfStudy.band.notStarted", average: null },
];

const CELL_CLASS = "px-2 py-2.5 text-center";
/** The name column stays put while the lessons scroll, so a row never loses whose it is. */
const NAME_CELL_CLASS = "sticky left-0 z-10 bg-white px-3 py-2.5 text-left";

function dotColor(average: number | null | undefined): string {
  return average == null ? NOT_STARTED_COLOR : getScoreColor(average);
}

function ScoreDot({ average, title }: { average: number | null | undefined; title: string }) {
  return (
    <span
      className="inline-block h-3.5 w-3.5 rounded-full align-middle"
      style={{ backgroundColor: dotColor(average) }}
      title={title}
      aria-label={title}
      role="img"
    />
  );
}

/** The row's own standing, in the same colour language the cells use. */
function TotalPill({ average }: { average: number | null | undefined }) {
  const color = dotColor(average);

  return (
    <span
      className="inline-flex h-[26px] min-w-[44px] items-center justify-center rounded-full px-2 font-kumbh font-bold text-[13px]"
      style={{ backgroundColor: `${color}1F`, color }}
    >
      {average ?? NO_SCORE_LABEL}
    </span>
  );
}

/** The columns are known before the scores are, so the header renders the same either way. */
function MatrixHead({ lessons }: { lessons: { lessonId: string; name: string }[] }) {
  const t = useT();

  return (
    <thead>
      <tr className="border-gray-100 border-b">
        <th scope="col" className={`${NAME_CELL_CLASS} app-label font-normal`}>
          {t["selfStudy.table.student"]}
        </th>
        {lessons.map((lesson) => (
          <th
            key={lesson.lessonId}
            scope="col"
            className="app-label max-w-[7rem] truncate px-2 py-2.5 font-normal"
            title={lesson.name}
          >
            {lesson.name}
          </th>
        ))}
        <th scope="col" className="app-label px-2 py-2.5 font-normal">
          {t["selfStudy.table.total"]}
        </th>
      </tr>
    </thead>
  );
}

function StudentRow({ row }: { row: MatrixRow }) {
  const t = useT();
  const hint = (slidesDone: number, slidesTotal: number) =>
    row.studentId
      ? format(t["report.lessons.slidesDone"], { done: slidesDone, total: slidesTotal })
      : t["selfStudy.noAccount"];

  return (
    <tr className="border-gray-100 border-b last:border-0 hover:bg-app-sky-soft">
      <th scope="row" className={NAME_CELL_CLASS}>
        <span className="block truncate font-kumbh font-bold text-[13px] text-app-ink">
          {row.name}
        </span>
        {row.englishName && (
          <span className="block truncate text-[11px] text-app-muted">{row.englishName}</span>
        )}
      </th>
      {row.lessons.map((cell) => (
        <td key={cell.lessonId} className={CELL_CLASS}>
          <ScoreDot average={cell.averageScore} title={hint(cell.slidesDone, cell.slidesTotal)} />
        </td>
      ))}
      <td className={CELL_CLASS}>
        <TotalPill average={row.totalAverage} />
      </td>
    </tr>
  );
}

/** A row's shape while the scores load: the same cells, unlit. */
function StudentRowSkeleton({ lessons }: { lessons: { lessonId: string; name: string }[] }) {
  return (
    <tr className="border-gray-100 border-b last:border-0">
      <th scope="row" className={NAME_CELL_CLASS}>
        <Skeleton className="h-4 w-28" />
        <Skeleton className="mt-1 h-3 w-16" />
      </th>
      {lessons.map((lesson) => (
        <td key={lesson.lessonId} className={CELL_CLASS}>
          <Skeleton className="mx-auto h-3.5 w-3.5 rounded-full" />
        </td>
      ))}
      <td className={CELL_CLASS}>
        <Skeleton className="mx-auto h-[26px] w-[44px] rounded-full" />
      </td>
    </tr>
  );
}

/**
 * A folder's lessons against the whole class: a row per student, a lesson per column, each cell
 * lit by that student's average over the slides they have scored there. Homework plays are left
 * out upstream, so the table is self-study only.
 */
export function ClassLessonTable({
  classroomId,
  group,
  onClose,
}: {
  classroomId: string;
  group: { name: string; lessons: { lessonId: string; name: string }[] };
  onClose: () => void;
}) {
  const t = useT();
  const { data, loading, error } = useQuery(ClassroomLessonMatrixDocument, {
    variables: { classroomId, lessonIds: group.lessons.map((lesson) => lesson.lessonId) },
  });

  const rows = data?.classroomLessonMatrix ?? [];

  return (
    <AppDialog
      label={t["selfStudy.progress.label"]}
      title={<h2 className="app-title min-w-0 truncate">{group.name}</h2>}
      actions={
        <AppButton tone="quiet" onClick={onClose}>
          {t["common.done"]}
        </AppButton>
      }
      size="lg"
      measure="max-w-6xl"
      onDismiss={onClose}
    >
      <div className="flex flex-wrap items-center gap-x-5 gap-y-1.5 px-1">
        {LEGEND.map((band) => (
          <span key={band.key} className="flex items-center gap-1.5 text-[11px] text-app-ink/70">
            <span
              className="h-3.5 w-3.5 rounded-full"
              style={{ backgroundColor: dotColor(band.average) }}
              aria-hidden="true"
            />
            {t[band.key]}
          </span>
        ))}
      </div>

      <div className="app-card overflow-x-auto p-0">
        {loading ? (
          <table className="w-full border-collapse">
            <MatrixHead lessons={group.lessons} />
            <tbody>
              {SKELETON_ROWS.map((key) => (
                <StudentRowSkeleton key={key} lessons={group.lessons} />
              ))}
            </tbody>
          </table>
        ) : error ? (
          <p className="body-sm p-4 text-app-overdue">{t["selfStudy.progress.loadError"]}</p>
        ) : rows.length === 0 ? (
          <p className="body-sm p-4 text-app-muted">{t["selfStudy.progress.empty"]}</p>
        ) : (
          <table className="w-full border-collapse">
            <MatrixHead lessons={group.lessons} />
            <tbody>
              {rows.map((row) => (
                <StudentRow key={row.myBestUserId} row={row} />
              ))}
            </tbody>
          </table>
        )}
      </div>
    </AppDialog>
  );
}
